import { HolidayKey } from "./types";

/**
 * Tailwind-färgskalor (v3). En färgklass per tema, aldrig blandat.
 * Nycklarna motsvarar color-50 ... color-950.
 */
export type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
export type Palette = Record<Shade, string>;

export const PALETTES: Record<"amber" | "green" | "sky" | "orange" | "red" | "indigo", Palette> = {
  amber: {
    50: "#fffbeb", 100: "#fef3c7", 200: "#fde68a", 300: "#fcd34d", 400: "#fbbf24",
    500: "#f59e0b", 600: "#d97706", 700: "#b45309", 800: "#92400e", 900: "#78350f",
    950: "#451a03",
  },
  green: {
    50: "#f0fdf4", 100: "#dcfce7", 200: "#bbf7d0", 300: "#86efac", 400: "#4ade80",
    500: "#22c55e", 600: "#16a34a", 700: "#15803d", 800: "#166534", 900: "#14532d",
    950: "#052e16",
  },
  sky: {
    50: "#f0f9ff", 100: "#e0f2fe", 200: "#bae6fd", 300: "#7dd3fc", 400: "#38bdf8",
    500: "#0ea5e9", 600: "#0284c7", 700: "#0369a1", 800: "#075985", 900: "#0c4a6e",
    950: "#082f49",
  },
  orange: {
    50: "#fff7ed", 100: "#ffedd5", 200: "#fed7aa", 300: "#fdba74", 400: "#fb923c",
    500: "#f97316", 600: "#ea580c", 700: "#c2410c", 800: "#9a3412", 900: "#7c2d12",
    950: "#431407",
  },
  indigo: {
    50: "#eef2ff", 100: "#e0e7ff", 200: "#c7d2fe", 300: "#a5b4fc", 400: "#818cf8",
    500: "#6366f1", 600: "#4f46e5", 700: "#4338ca", 800: "#3730a3", 900: "#312e81",
    950: "#1e1b4b",
  },
  red: {
    50: "#fef2f2", 100: "#fee2e2", 200: "#fecaca", 300: "#fca5a5", 400: "#f87171",
    500: "#ef4444", 600: "#dc2626", 700: "#b91c1c", 800: "#991b1b", 900: "#7f1d1d",
    950: "#450a0a",
  },
};

export type PaletteName = keyof typeof PALETTES;

export interface HolidayConfig {
  key: HolidayKey;
  /** Svenskt namn, används i footerns testlänkar. */
  label: string;
  /** Hälsningen som visas på inställningssidan. */
  greeting: string;
  emoji: string;
  paletteName: PaletteName;
  palette: Palette;
  /**
   * Temats enda utseende, oavsett användarens ljust/mörkt-val.
   * Påsk är en ljus vårmorgon, övriga teman är kvällar/nätter.
   */
  look: "light" | "dark";
  /**
   * Knappfärger. Endast färg ändras på knapparna, aldrig mått.
   * Textfärgen är color-950 eller vit beroende på vad som ger bäst kontrast.
   */
  button: {
    light: { bg: string; fg: string };
    dark: { bg: string; fg: string };
  };
}

const make = (
  key: HolidayKey,
  label: string,
  greeting: string,
  emoji: string,
  paletteName: PaletteName,
  lightButtonShade: Shade,
  lightButtonFg: "white" | "dark",
  look: "light" | "dark" = "dark"
): HolidayConfig => {
  const palette = PALETTES[paletteName];
  return {
    key,
    label,
    greeting,
    emoji,
    paletteName,
    palette,
    look,
    button: {
      light: {
        bg: palette[lightButtonShade],
        fg: lightButtonFg === "white" ? "#ffffff" : palette[950],
      },
      // I darkmode är knapparna ljusa mot den mörka bakgrunden -> mörk text.
      dark: { bg: palette[400], fg: palette[950] },
    },
  };
};

export const HOLIDAYS: Record<HolidayKey, HolidayConfig> = {
  easter: make("easter", "Påsk", "Glad påsk!", "🐣", "amber", 400, "dark", "light"),
  midsummer: make("midsummer", "Midsommar", "Glad midsommar!", "🌼", "green", 600, "white"),
  halloween: make("halloween", "Halloween", "Happy Halloween!", "🎃", "orange", 600, "white"),
  lucia: make("lucia", "Lucia", "Glad Lucia!", "🕯️", "indigo", 600, "white"),
  christmas: make("christmas", "Jul", "God jul!", "🎅", "red", 600, "white"),
};

/**
 * Färger som hjulet använder. Segmenten har samma ljusa nyans i både light-
 * och darkmode. Rutnätet (segmentkanterna) och kanten runt hjulet är
 * tydligt mörkare så att hjulet aldrig smälter in i bakgrunden – särskilt
 * viktigt i ljust läge där bakgrunden också är ljus.
 */
export const wheelColors = (holiday: HolidayKey, dark: boolean) => {
  const p = HOLIDAYS[holiday].palette;
  if (HOLIDAYS[holiday].look === "light") {
    // Ljust tema: varm, gyllene mitt i stället för mörkbrunt.
    return {
      segmentFrom: "#ffffff",
      segmentTo: p[100],
      segmentStroke: p[500],
      rim: p[400],
      rimLight: p[200],
      rimDark: p[600],
      text: p[950],
      centerFrom: p[300],
      centerTo: p[500],
    };
  }
  return {
    segmentFrom: p[50],
    segmentTo: p[200],
    segmentStroke: dark ? p[800] : p[600],
    rim: dark ? p[800] : p[600],
    rimLight: p[300],
    rimDark: p[950],
    text: p[950],
    centerFrom: p[800],
    centerTo: p[950],
  };
};

/** Bakgrundsfärgen som används när hjulet sparas som bild. */
export const captureBackground = (holiday: HolidayKey, dark: boolean) => {
  const p = HOLIDAYS[holiday].palette;
  return dark ? p[950] : p[50];
};
